import { useEffect, useState } from 'react'

import { Skeleton } from '@mui/material'
import { usePermissions } from 'commons/customHooks/useAuth'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { getUser } from 'commons/utils/user'
import { DatePicker, SectionCard, SlimButton } from 'components'
import type { ActionPerm } from 'components/Structures/Header/constants'
import dayjs from 'dayjs'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { oVMaintenance } from 'services/manage_ov'
import type { OvMaintenanceResponse } from 'services/manage_ov/interface'
import { setOvDetailedInfo } from 'store/OVContext/ovSlice'
import { useDispatch, useSelector } from 'store/store'

import * as S from './styles'

// TODO: replace direct dayjs() usage with utils

export const BillingSection = () => {
  const {
    ovSelected: { Cabecalho },
    detailedInfo,
    detailedInfo: { FaturamentoEntrega },
    loadingDetailedInfo: loading,
  } = useSelector((state) => state.ov)

  const { ovNumber } = useParams()
  const dispatch = useDispatch()
  const [loadingSave, setLoadingSave] = useState(false)

  const defaultValues = {
    dtDesejadaCliente: FaturamentoEntrega?.DataDesejadaCliente,
    prevFaturamento: FaturamentoEntrega?.DataPrevisaoFaturamento,
  }

  const {
    control,
    watch,
    formState: { isDirty },
    handleSubmit,
    reset,
  } = useForm({
    defaultValues,
  })

  const currentPrevDate = watch('prevFaturamento')

  const resetForm = () => {
    reset({
      dtDesejadaCliente: FaturamentoEntrega.DataDesejadaCliente,
      prevFaturamento: FaturamentoEntrega.DataPrevisaoFaturamento,
    })
  }

  useEffect(() => {
    if (!loading) {
      resetForm()
    }
  }, [loading])

  const { verifyPermission } = usePermissions()

  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.OV, action) || +Cabecalho.CodigoStatus !== 0
  }

  const onSave = async (data: typeof defaultValues) => {
    const requestBody = {
      CodigoEntrega: Cabecalho.CodigoSapCliente,
      CodigoUsuarioAlteracao: getUser(),
      CondicaoPagamento: Cabecalho.CondicaoPagamento,
      // TODO: Replace momentjs with functions from commons/utils/date
      DataDesejadaCliente: dayjs(data.dtDesejadaCliente).format('YYYY-MM-DD'),
      DataPrevistaFaturamento: dayjs(data.prevFaturamento).format('YYYY-MM-DD'),
      DiasAdicionais: Cabecalho.PrazoExtra,
      NumeroOrdemVenda: `${ovNumber}`,
      NumeroPedidoCliente: Cabecalho.NumeroPedidoCliente,
      TipoOrdem: Cabecalho.CodigoTipoOrdem,
    }
    setLoadingSave(true)
    try {
      const res: OvMaintenanceResponse = await oVMaintenance(`${ovNumber}`, requestBody)
      if (res.OperacaoRealizada) {
        dispatch(
          setOvDetailedInfo({
            ...detailedInfo,
            FaturamentoEntrega: {
              ...FaturamentoEntrega,
              DataDesejadaCliente: requestBody.DataDesejadaCliente,
              DataPrevisaoFaturamento: requestBody.DataPrevistaFaturamento,
            },
          }),
        )
        reset({
          dtDesejadaCliente: requestBody.DataDesejadaCliente,
          prevFaturamento: requestBody.DataPrevistaFaturamento,
        })
        toast.success('Ordem de Venda editada com sucesso.')
      } else {
        resetForm()
        toast.error(res?.Mensagem || `Não foi possível editar a Ordem de Venda.`)
      }
    } catch (error) {
      resetForm()
      toast.error(`Erro ao editar a Ordem de Venda.`)
    } finally {
      setLoadingSave(false)
    }
  }

  const RenderSkeleton = () => (
    <S.Wrapper>
      <Skeleton height={40} />
      <Skeleton height={40} />
      <Skeleton height={40} />
      <Skeleton height={40} />
      <Skeleton height={40} />
    </S.Wrapper>
  )

  return (
    <SectionCard
      title="Datas de faturamento e entrega"
      tooltipText="Nesta seção você pode ver as datas calculadas pelo sistema referentes ao faturamento e a entrega da OV. Também é possível programar datas específicas para essas áreas, de acordo com a necessidade do cliente."
    >
      {loading ? (
        <RenderSkeleton />
      ) : (
        <S.Wrapper onSubmit={handleSubmit(onSave)}>
          <Controller
            control={control}
            name="prevFaturamento"
            render={({ field, fieldState }) => (
              <DatePicker
                {...field}
                disabled={shouldDisable(ACTIONS_SCREENS.SAVE)}
                disablePast
                error={fieldState.error?.message}
                label="Dt. prev. faturamento"
                maxDate={dayjs(field.value).endOf('month') as unknown as string}
              />
            )}
            rules={{
              validate: {
                max: (date) =>
                  dayjs(date).isBefore(dayjs().endOf('month')) || 'Data fora do período válido.',
                min: (date) =>
                  dayjs(date).endOf('day').isAfter(dayjs().startOf('day')) ||
                  'Data fora do período válido.',
              },
            }}
          />
          <DatePicker
            disabled
            label="Entrega calculada"
            onChange={() => ''}
            value={FaturamentoEntrega.DataEntregaCalculada}
          />
          <DatePicker
            disabled
            label="Dt. fatura calculada"
            onChange={() => ''}
            value={FaturamentoEntrega.DataFaturamentoCalculado}
          />
          <Controller
            control={control}
            name="dtDesejadaCliente"
            render={({ field, fieldState }) => (
              <DatePicker
                {...field}
                disabled={shouldDisable(ACTIONS_SCREENS.SAVE)}
                error={fieldState.error?.message}
                label="Dt. desejada cliente"
                maxDate={dayjs(currentPrevDate).add(120, 'd') as unknown as string}
                minDate={dayjs(FaturamentoEntrega.DataEntregaCalculada) as unknown as string}
              />
            )}
            rules={{
              validate: {
                max: (date) =>
                  dayjs(date)
                    .startOf('day')
                    .isBefore(dayjs(currentPrevDate).add(120, 'd').endOf('day')) ||
                  'Data fora do período válido.',
                min: (date) =>
                  dayjs(date)
                    .endOf('day')
                    .isAfter(dayjs(FaturamentoEntrega.DataEntregaCalculada).startOf('day')) ||
                  'Data fora do período válido.',
              },
            }}
          />
          <DatePicker
            disabled
            label="Dt. fatura combinada"
            onChange={() => ''}
            value={FaturamentoEntrega.DataFaturamentoCombinado}
          />
          {isDirty && (
            <S.ActionsRow>
              <SlimButton content="Salvar" loading={loadingSave} type="submit" variant="primary" />
              <SlimButton
                content="Desfazer"
                disabled={loadingSave}
                onClick={resetForm}
                type="button"
                variant="secondary"
              />
            </S.ActionsRow>
          )}
        </S.Wrapper>
      )}
    </SectionCard>
  )
}
